// The availability panel (Booking V3) for option-led services: what → how
// many → when. The guest picks the option(s) and headcount on the cards, then
// sees — per tenant-local day — the bands in which that whole choice can start
// (get_service_availability_bands), picks a start, and adds the visit in one
// go: every option at the same start, all or nothing (state/visit-holds.ts).
//
// Built from the shared booking pieces (UnitCard, ServiceHero, the seeded-date
// confirm, the hold-failure banner, holdUnit + buildUnitCartLine), never
// copies. WidgetShell mounts it for services that pass
// routesToAvailabilityPanel (state/option-led.ts); everything else keeps
// ServicePanel.

import { useLinkPromoPreview, useLinkPromoPicked } from "../state/link-promo-preview";
import { pickedLineAfterLinkPromo } from "../state/link-promo";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from "react";
import type { WidgetBrand, WidgetService, WidgetTenant, WidgetUnit } from "../types";
import { useCart } from "../state/cart";
import { stampVisitPartySize } from "../state/booking";
import { hoursPillText, useServiceHours } from "../state/service-hours";
import { optionPriceSuffix, unitPricingBasis, useUnitWindowPrices } from "../state/unit-pricing";
import { getTripDate, getTripInstantMs, setTripDateFromIso } from "../state/trip-date";
import { pushToast } from "../state/toast";
import { useServiceBookingWindow } from "../state/booking-window";
import { useAvailabilityBands } from "../state/useAvailabilityBands";
import { useAloneBands } from "../state/useAloneBands";
import { useUnitBlackouts } from "../state/useUnitBlackouts";
import { unitsNotOfferedOn } from "../state/unit-blackouts";
import { placeVisit, refusalMessage, type VisitEntry } from "../state/visit-holds";
import { useNow } from "../state/use-now";
import {
  MAX_ENTRIES,
  buildEntries,
  guestNoun,
  lengthLabel,
  optionDurationChoices,
  optionDurationMinutes,
  optionShape,
  oversizeBlockCap,
  oversizeEnquiryAbove,
  partyCapFor,
  partyMaxFor,
  partyMinFor,
  startGranularity,
  tierPrice,
  unitCap,
  unitDurationMinutes,
} from "../state/option-led";
import {
  browserDateOfYmd,
  formatHmUnambiguous,
  offsetMinAt,
  tenantTz,
  todayYmdIn,
  ymdOfInstant,
  zonedDayWindows,
  zonedNoonIso,
  zonedWallMs,
} from "../state/tenant-time";
import {
  addDaysYmd,
  bandStarts,
  clampJump,
  containsStart,
  fixedStarts,
  dayLabel,
  DAYS_PER_PAGE,
  dayStatus,
  emptyDayCopy,
  horizonEnd,
  pageDays,
  planVisitAdd,
  visitLinesAt,
  type Band,
  type DayStatus,
} from "../state/availability-logic";
import {
  aloneQueries,
  dayAlone,
  togetherNudge,
  togetherStatus,
  togetherVerb,
  type DayAlone,
  type TogetherAction,
  type TogetherOption,
} from "../state/together-logic";
import {
  dayTile,
  droppedStartText,
  fixedDayStatus,
  fixedStartMinutes,
  tileMode,
  type DayTile,
  type TileMode,
} from "../state/day-tile-logic";
import {
  addRevealPlan,
  nextStep,
  readyAnnouncement,
  stepStateSpoken,
  type NextStep,
  type StepState,
} from "../state/next-step-logic";
import { ContinuingBanner, ServiceHero, renderInCartSummary } from "./booking/ServiceHero";
import { SeededDateConfirmModal, useSeededDateConfirm } from "./booking/SeededDateConfirm";
import { WhatStep } from "./availability/WhatStep";
import { DayStrip } from "./availability/DayStrip";
import { DayTimeline } from "./availability/DayTimeline";
import { StartPicker } from "./availability/StartPicker";
import { VisitSummary, type PanelNotice, type SummaryLine } from "./availability/VisitSummary";

export interface AvailabilityPanelProps {
  service: WidgetService;
  tenant: WidgetTenant;
  activeBrand: WidgetBrand | null;
  heroBrand?: WidgetBrand | null;
  heroStyle?: CSSProperties;
  panelStyle?: CSSProperties;
  continuingFromServiceName?: string | null;
  onDismissContinuing?: () => void;
  /** After a visit is added: WidgetShell's "Who's booking?" → cart drawer. */
  onAddedToCart?: () => void;
  /** The service's enquiry route (large parties under the "enquiry" policy). */
  onEnquire?: () => void;
  /** A ?option= link's option, chosen on mount (WidgetShell, one-shot). */
  initialOptionId?: string | null;
  onConsumeInitialOption?: () => void;
}

type Phase = "idle" | "checking" | "holding";

/**
 * The shared trip date as a VENUE day. The trip date itself is the browser's
 * day of the instant it was set from (trip-date.ts); read through that
 * instant, a guest hours away from the venue still opens on the venue day of
 * the visit they picked.
 */
function tripDayIn(tz: string): string | null {
  const at = getTripInstantMs();
  return at != null ? ymdOfInstant(at, tz) : getTripDate();
}

// The notice for an Add that finished after its panel unmounted (the guest
// switched tabs mid-Add). A kept line (release unconfirmed) is already in the
// visit, so its explanation must not be lost with the panel: the service's
// live panel shows it, or the next one to mount does.
const orphanNotices = new Map<string, PanelNotice>();
const noticeListeners = new Map<string, (n: PanelNotice) => void>();
function deliverOrphanNotice(serviceId: string, notice: PanelNotice) {
  const live = noticeListeners.get(serviceId);
  if (live) live(notice);
  else orphanNotices.set(serviceId, notice);
}

export function AvailabilityPanel({
  service,
  tenant,
  activeBrand,
  heroBrand,
  heroStyle,
  panelStyle,
  continuingFromServiceName,
  onDismissContinuing,
  onAddedToCart,
  onEnquire,
  initialOptionId = null,
  onConsumeInitialOption,
}: AvailabilityPanelProps) {
  const { add, patchMetadata, items: cartItems } = useCart();
  const linkPromo = useLinkPromoPreview();
  // The link's code on the ride still being picked (nothing held yet).
  const linkPicked = useLinkPromoPicked();
  const tz = tenantTz(tenant);
  const offsetAt = useMemo(() => offsetMinAt(tz), [tz]);
  const shape = optionShape(service);
  const gran = startGranularity(service);
  const noun = guestNoun(service);
  const bw = useServiceBookingWindow(service.id);
  const noticeMin = Math.max((bw.minLeadHours ?? 0) * 60, bw.minLeadMinutes ?? 0);
  const hours = useServiceHours(service.id);
  const now = useNow(60_000);
  const todayYmd = todayYmdIn(tz, now);
  const endYmd = horizonEnd(todayYmd, bw.maxAdvanceDays);
  const showPrices = service.show_prices_in_widget !== false;
  const panelRef = useRef<HTMLElement | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const cartItemsRef = useRef(cartItems);
  cartItemsRef.current = cartItems;

  // ── What + how many ────────────────────────────────────────────────────────
  // A PER-ITEM option is sold here too (owner decision, 22 September). It used
  // to read "Not offered", with only a console warning, while the staff app and
  // the assistant went on selling it — an outage only the widget had.
  //
  // Nothing had to change for the money: a per-item hold is priced once for the
  // booking however many guests it carries, which is exactly what "a bay at
  // R450 for the group" means, and the visit summary already totals such a line
  // once (it reads each entry's own basis). The count on the card stays what it
  // says it is — guests — because that is what the engine reserves stock for.
  //
  // What is NOT offered: an option whose fewest guests per booking
  // (units[].party_size_min) is above the most it can take here — no count
  // on it could ever be held.

  const [sessionUnitId, setSessionUnitId] = useState<string | null>(null);
  // Sessions: the headcount starts at 2, and never below the picked option's
  // own minimum (chosenCounts floors it).
  const [headcount, setHeadcount] = useState(2);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [unitMinutes, setUnitMinutes] = useState<Record<string, number>>({});

  const blockCap = oversizeBlockCap(service);
  const enquiryAbove = oversizeEnquiryAbove(service);
  const capFor = (u: WidgetUnit) =>
    Math.max(1, Math.min(shape === "sessions" ? partyCapFor(service, u) : unitCap(u), blockCap ?? 100, 100));
  // The fewest guests per booking (2 for "2–4 riders"; 1 when there is no
  // minimum). The hold refuses fewer (party_below_minimum), so no stepper
  // offers fewer.
  const minFor = (u: WidgetUnit) => partyMinFor(service, u);
  const offered = (u: WidgetUnit) => minFor(u) <= capFor(u);
  const lengthFor = (u: WidgetUnit) => optionDurationMinutes(service, u, unitMinutes[u.id]);

  // A ?option= link's option, chosen as the guest's own Choose would: a
  // ticked option at its minimum, or the session with the headcount floored
  // to it. An option the panel does not offer is left unchosen. The row
  // brings the linked card into view (WhatStep revealUnitId).
  const [linkedUnitId, setLinkedUnitId] = useState<string | null>(null);
  useEffect(() => {
    if (!initialOptionId) return;
    onConsumeInitialOption?.();
    const u = service.units.find((x) => x.id === initialOptionId);
    if (!u || !offered(u)) return;
    if (shape === "sessions") {
      setSessionUnitId(u.id);
      setHeadcount((h) => Math.min(Math.max(minFor(u), h), capFor(u)));
    } else {
      setCounts({ [u.id]: minFor(u) });
    }
    setLinkedUnitId(u.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOptionId]);

  const sessionUnit =
    shape === "sessions"
      ? (service.units.find((u) => u.id === sessionUnitId && offered(u)) ?? null)
      : null;
  const chosenCounts: Record<string, number> = {};
  if (shape === "sessions") {
    if (sessionUnit)
      chosenCounts[sessionUnit.id] = Math.min(Math.max(minFor(sessionUnit), headcount), capFor(sessionUnit));
  } else {
    for (const u of service.units) {
      const n = counts[u.id] ?? 0;
      if (n > 0 && offered(u)) chosenCounts[u.id] = Math.min(Math.max(n, minFor(u)), capFor(u));
    }
  }
  const countsSig = JSON.stringify(chosenCounts);
  const minutesSig = JSON.stringify(unitMinutes);
  const selection = useMemo(
    () => buildEntries(service, { counts: chosenCounts, minutes: unitMinutes }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service, countsSig, minutesSig],
  );
  const entries: VisitEntry[] = useMemo(
    () =>
      (selection ?? []).flatMap((s) => {
        const unit = service.units.find((u) => u.id === s.service_unit_id);
        if (!unit) return [];
        return [
          {
            unit,
            guests: s.guests,
            minutes: optionDurationMinutes(service, unit, unitMinutes[unit.id]),
            variableLength: unitDurationMinutes(unit) == null && optionDurationChoices(unit).length > 0,
            basis: unitPricingBasis(service, unit),
          },
        ];
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection, service],
  );
  const longestMin = entries.reduce((m, e) => Math.max(m, e.minutes), 0);
  const tooMany = entries.length > MAX_ENTRIES;
  // The cap is a statement about the VISIT, not about one option (engine:
  // _booking_service_visit_party, 20260923090010). "4 on Meadow + 4 on Ridge"
  // is a party of eight, so what the guest has picked is added up — and below,
  // once the cart is in hand, so is what this visit already holds at this
  // start. Reading it per line offered an Add the hold then refused.
  const pickedGuests = entries.reduce((n, e) => n + e.guests, 0);
  // The bands are asked about the selection, so the selection alone decides
  // whether asking is pointless.
  const selectionOversize =
    (enquiryAbove != null && pickedGuests > enquiryAbove) ||
    (blockCap != null && pickedGuests > blockCap);
  const bandSelection = selection && !tooMany && !selectionOversize ? selection : null;

  // ── When ───────────────────────────────────────────────────────────────────
  const cartSig = useMemo(
    () => cartItems.map((i) => String(i.metadata?.booking_id ?? i.id)).sort().join("|"),
    [cartItems],
  );
  const [pageFrom, setPageFrom] = useState<string>(() => {
    const today = todayYmdIn(tz);
    const trip = tripDayIn(tz);
    return trip && trip >= today && trip <= horizonEnd(today, null) ? trip : today;
  });
  // Keep the page inside [today, horizon] as the clock and the window move.
  useEffect(() => {
    if (pageFrom < todayYmd) setPageFrom(todayYmd);
    else if (pageFrom > endYmd) setPageFrom(clampJump(endYmd, todayYmd, endYmd));
  }, [pageFrom, todayYmd, endYmd]);
  const page = pageDays(pageFrom, endYmd);
  const bands = useAvailabilityBands({
    serviceId: service.id,
    selection: bandSelection,
    fromYmd: page.days.length ? pageFrom : null,
    days: page.days.length,
    cartSig,
  });
  const bandsStatus = bands.state.status;
  const unitSchedule = useUnitBlackouts(service.id, bands.key);
  const readyDays = bands.state.status === "ready" ? bands.state.days : null;
  const noticeKnown = bands.state.status === "ready" ? bands.state.noticeKnown : true;

  const [day, setDay] = useState<string | null>(null);
  const [dayChosenByGuest, setDayChosenByGuest] = useState(false);
  const [bandOpensMs, setBandOpensMs] = useState<number | null>(null);
  const [startMs, setStartMs] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [notice, setNotice] = useState<PanelNotice | null>(null);
  useEffect(() => {
    const pending = orphanNotices.get(service.id);
    if (pending) {
      orphanNotices.delete(service.id);
      setNotice(pending);
    }
    const listener = (n: PanelNotice) => setNotice(n);
    noticeListeners.set(service.id, listener);
    return () => {
      if (noticeListeners.get(service.id) === listener) noticeListeners.delete(service.id);
    };
  }, [service.id]);
  const inFlightRef = useRef(false);
  const busy = phase !== "idle";

  const windowsFor = (d: string) => zonedDayWindows(hours, d, tz);
  const notOfferedFor = (d: string) => unitsNotOfferedOn(d, entries.map((e) => e.unit.id), unitSchedule.rules);
  // The fixed start times the page's other days show, on today's clock: a
  // fresh read of today after its last start has no band at all, and these
  // tell "the day is over" (Too soon) from "Full" (day-tile-logic.ts).
  const todayStartsMs = fixedStartMinutes(
    page.days.filter((d) => d !== todayYmd).map((d) => readyDays?.[d]?.bands),
    offsetAt,
  ).map((m) => zonedWallMs(todayYmd, m, tz));
  const statusFor = (d: string): DayStatus => {
    const w = windowsFor(d);
    const s = dayStatus({
      day: d,
      todayYmd,
      request: bandsStatus === "ready" ? "ready" : bandsStatus === "loading" ? "loading" : bandsStatus === "error" ? "error" : "idle",
      bands: readyDays?.[d]?.bands,
      gran,
      offsetAt,
      closedDates: bw.closedDates,
      hasHours: hours.length > 0,
      openWindowCount: w.length,
      noticeMin,
      nowMs: now,
      lastWindowEndMs: w.length ? Math.max(...w.map((x) => x.endMs)) : null,
      longestMin,
      scheduleRequest: unitSchedule.status,
      notOffered: notOfferedFor(d).length > 0,
    });
    // A day of fixed starts the clock has overtaken has nothing left: never "Free".
    return fixedDayStatus({
      status: s,
      bands: readyDays?.[d]?.bands,
      nowMs: now,
      isToday: d === todayYmd,
      todayStartsMs,
      noticeMin,
    });
  };
  const baseStatuses: Record<string, DayStatus> = {};
  for (const d of page.days) baseStatuses[d] = statusFor(d);

  // ── Not at the same time (together-logic.ts) ───────────────────────────────
  // Two or more options with no start together on a day may still each have
  // starts alone (Fijnland: one outride guide, more horses). Only then — some
  // day of the page came back "full" or "not_offered" for the combination — ask each option
  // alone, once per page and question, so that day reads "Go together" or
  // "One at a time" instead of "Full".
  const togetherOptions: TogetherOption[] = entries.map((e) => ({
    id: e.unit.id,
    name: e.unit.name,
    guests: e.guests,
    min: minFor(e.unit),
    max: capFor(e.unit),
  }));
  const combinationNeedsAlternatives =
    shape === "multi" && entries.length >= 2 && !!bandSelection &&
    page.days.some((d) => baseStatuses[d] === "full" || baseStatuses[d] === "not_offered");
  const aloneQs = combinationNeedsAlternatives && bandSelection ? aloneQueries(bandSelection, togetherOptions) : null;
  const alone = useAloneBands({
    serviceId: service.id,
    queries: aloneQs,
    fromYmd: page.days.length ? pageFrom : null,
    days: page.days.length,
    cartSig,
  });
  const aloneOn = (d: string): DayAlone | "pending" | null => {
    if (!combinationNeedsAlternatives) return null;
    if (alone.status === "loading") return "pending";
    if (alone.status !== "ready") return null;
    const answers = alone.answers;
    return dayAlone({
      bandsFor: (k) => answers[k]?.[d]?.bands,
      options: togetherOptions,
      gran,
      offsetAt,
      nowMs: now,
    });
  };
  const statuses: Record<string, DayStatus> = {};
  for (const d of page.days) statuses[d] = togetherStatus(baseStatuses[d]!, aloneOn(d));

  // ── Start times in the day tiles (day-tile-logic.ts) ──────────────────────
  // A day of fixed start times lists them in its own tile, with the length
  // once: one tap picks the day and the start, and no time strip or start
  // row follows. Ranged days keep the status word, the strip and the chips.
  // The layout follows what the bands ARE (fixed starts or ranges), read from
  // the choice's own answer and, on a day two options can't share, from each
  // option alone — so a "Ride together" day of fixed-start rides has no time
  // strip either.
  const tileModeRef = useRef<TileMode>("status");
  const modeEvidence: (Band[] | undefined)[] = page.days.map((d) => readyDays?.[d]?.bands);
  if (alone.status === "ready") {
    for (const byDay of Object.values(alone.answers)) for (const d of page.days) modeEvidence.push(byDay?.[d]?.bands);
  }
  const mode = tileMode(modeEvidence, tileModeRef.current);
  tileModeRef.current = mode;
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
  const tileMinutes = entries.map((e) => e.minutes);
  const tiles: Record<string, DayTile> = {};
  for (const d of page.days) {
    tiles[d] = dayTile({
      status: statuses[d]!,
      bands: readyDays?.[d]?.bands,
      nowMs: now,
      minutes: tileMinutes,
      selectedStart: d === day ? startMs : null,
      expanded: !!expandedDays[d],
      noun,
    });
  }
  // The chosen day offers its starts in its tile: nothing under the row.
  const dayInTile = mode === "starts" && !!day && tiles[day]?.kind === "starts";

  // The seeded-date confirm: asked only when the PANEL picked the day (the
  // guest never touched the strip), naming that day. Keyed on the choice, so
  // a change while it is open cancels it.
  const { setSeededStartsAt, dateConfirm, setDateConfirm, requestDateConfirm, forgetDateConfirmed } =
    useSeededDateConfirm(JSON.stringify([service.id, selection, day, startMs]));
  // The panel picks a day for the guest: Add asks about THIS day, even after
  // a "Yes" given for an earlier panel-picked day (that answer was for it).
  const seedPanelDay = (ymd: string) => {
    forgetDateConfirmed();
    setSeededStartsAt(zonedNoonIso(ymd, tz));
  };

  // Preselect a day once the first page has answered (or failed — then the
  // fail-open times still need a day): the trip date if it is on the page,
  // else the first free day, else the page's first day.
  useEffect(() => {
    if (day && page.days.includes(day) && day >= todayYmd) return;
    if (bandsStatus !== "ready" && bandsStatus !== "error") return;
    const trip = tripDayIn(tz);
    const pick =
      (trip && page.days.includes(trip) ? trip : undefined) ??
      (bandsStatus === "ready" ? page.days.find((d) => statuses[d] === "free") : undefined) ??
      page.days[0];
    if (!pick) return;
    setDay(pick);
    setDayChosenByGuest(false);
    seedPanelDay(pick);
    setBandOpensMs(null);
    setStartMs(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bandsStatus, pageFrom, day, todayYmd]);

  const dayBands: Band[] = day && readyDays?.[day] ? readyDays[day].bands : [];
  const pickable = dayBands.filter((b) => bandStarts(b, gran, offsetAt, now).length > 0);
  const dayFixed = fixedStarts(dayBands, now);
  const selectedBand: Band | null = useMemo(() => {
    if (!pickable.length) return null;
    if (startMs != null) {
      const b = pickable.find((x) => x.opensAtMs <= startMs && startMs <= x.lastStartMs);
      if (b) return b;
    }
    return pickable.find((x) => x.opensAtMs === bandOpensMs) ?? pickable[0]!;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyDays, day, startMs, bandOpensMs, gran, now]);
  const dayStat: DayStatus | null = day ? (statuses[day] ?? togetherStatus(statusFor(day), aloneOn(day))) : null;
  // Fail open only where the panel does not already know better: a day
  // closed in the hours or the closed dates stays Closed, and a today the
  // known notice rules out stays Too soon — neither offers times.
  const failOpen = !!day && !!bandSelection && bandsStatus === "error" &&
    unitSchedule.status === "ready" && dayStat === "unknown";

  // A start that the fresh bands no longer contain is dropped. A selection
  // change keeps the start when the new bands still contain it.
  //
  // "… is no longer free" is said only when the bands for the SAME choice
  // dropped it (someone took it). When the guest's own change of choice
  // dropped it, that sentence was false and outlived its cause (a guest who
  // ticked a second ride saw "08:30 is no longer free" beside a day that had
  // nothing for the pair): the start just goes, and the day's own words say
  // what is free for the new choice.
  const choiceSig = bandSelection ? JSON.stringify(bandSelection) : "";
  const startChoiceRef = useRef<string | null>(null);
  useEffect(() => {
    if (startMs == null || busy || !day || !readyDays) return;
    const db = readyDays[day];
    if (!db) return;
    if (containsStart(db.bands, startMs)) {
      // readyDays answers the current choice: the start holds for it.
      startChoiceRef.current = choiceSig;
      return;
    }
    setStartMs(null);
    if (startChoiceRef.current !== choiceSig) return;
    // A refusal's message (it names what was refused) outranks this one.
    const text = droppedStartText(formatHmUnambiguous(startMs, tz), startMs, now);
    setNotice((n) => (n?.tone === "error" ? n : { tone: "info", text }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyDays, day, startMs, busy]);

  // While a start is chosen, keep the day fresh.
  useEffect(() => {
    if (startMs == null || !day || !bandSelection) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible" && !inFlightRef.current) void bands.recheckDay(day);
    }, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMs, day, bands.key]);

  // ── Prices (display only; the hold returns the price it charged) ─────────────
  const firstStartOfDay = selectedBand ? (bandStarts(selectedBand, gran, offsetAt, now)[0] ?? null) : null;
  const repStartMs = !day
    ? null
    : (startMs ??
      firstStartOfDay ??
      (windowsFor(day)[0]?.startMs ?? zonedWallMs(day, 12 * 60, tz)));
  const priceWindow = repStartMs != null
    ? {
        starts_at: new Date(repStartMs).toISOString(),
        ends_at: new Date(repStartMs + Math.max(longestMin, 1) * 60_000).toISOString(),
        party_size: 1,
      }
    : null;
  const windowSig = `${repStartMs ?? ""}|${minutesSig}|${countsSig}`;
  const prices = useUnitWindowPrices({
    units: service.units,
    window: priceWindow,
    showPrices: service.show_prices_in_widget,
    windowFor: (u: WidgetUnit) => ({
      starts_at: new Date(repStartMs ?? 0).toISOString(),
      ends_at: new Date((repStartMs ?? 0) + lengthFor(u) * 60_000).toISOString(),
      party_size: Math.max(1, chosenCounts[u.id] ?? 1),
    }),
    windowSig,
  });
  // UNROUNDED: the window quote when it answered, else the undated price (the
  // chosen length's tier, else base). Display rounds; storage never does.
  const unitPrice = (u: WidgetUnit): number | null =>
    prices.seasonalNightly[u.id] ?? tierPrice(u, lengthFor(u)) ?? (u.base_price != null ? Number(u.base_price) : null);

  // ── Add ──────────────────────────────────────────────────────────────────
  // What is already in the visit at the chosen start. The cart says, not this
  // panel: a panel that closed and reopened has forgotten its own Adds and any
  // line it kept after a refusal. An option held there is not held again.
  const inVisitLines = startMs != null ? visitLinesAt(cartItems, service.id, startMs) : [];
  const visitPlan = planVisitAdd(entries, inVisitLines);
  // What the engine will count: the options being held now, plus the ones this
  // visit already holds at this start. An option that is BOTH (already in, and
  // picked again) is counted once — planVisitAdd holds it only once.
  const heldElsewhereInVisit = inVisitLines.reduce((n, l) => {
    const p = l.metadata?.party_size;
    return !l.metadata?.combo_id &&
        typeof p === "number" &&
        !entries.some((e) => e.unit.id === l.unitId)
      ? n + p
      : n;
  }, 0);
  const visitGuests = pickedGuests + heldElsewhereInVisit;
  const oversize = enquiryAbove != null && visitGuests > enquiryAbove;
  const overBlock = blockCap != null && visitGuests > blockCap;
  const alreadyInIds = new Set(visitPlan.alreadyIn.map((e) => e.unit.id));
  const inVisitGuestsOf = (unitId: string) =>
    inVisitLines.reduce((n, l) => {
      const p = l.metadata?.party_size;
      return l.unitId === unitId && !l.metadata?.combo_id && typeof p === "number" ? n + p : n;
    }, 0);

  // Per-guest OR once-per-booking questions (booking_fields, 20260926090010).
  const participantForms = (() => {
    const c = service.config as { participant_fields?: unknown; booking_fields?: unknown } | null;
    const asks = (f: unknown) => Array.isArray(f) && f.length > 0;
    return asks(c?.participant_fields) || asks(c?.booking_fields);
  })();

  // Focus an Add dropped (its button turned off: the start passed or was just
  // taken) goes to the refreshed start times — the chip the arrow keys start
  // from, else the "any time" field, the start windows, the day's "Next
  // day →", or the day itself — never to <body>.
  const [refocusTick, setRefocusTick] = useState(0);
  const refocusStarts = () => setRefocusTick((n) => n + 1);
  useEffect(() => {
    if (refocusTick === 0) return;
    const root = panelRef.current;
    if (!root) return;
    const active = document.activeElement as HTMLElement | null;
    // Never pull focus from where the guest has since moved it.
    if (active && active !== document.body && !active.classList.contains("vt-avail-add")) return;
    const target =
      root.querySelector<HTMLElement>('.vt-avail-starts [role="radio"][tabindex="0"]') ??
      // Start times in the chosen day's tile, else in any tile.
      root.querySelector<HTMLElement>('.vt-avail-day-on .vt-avail-tstart[aria-pressed="true"]') ??
      root.querySelector<HTMLElement>(".vt-avail-day-on .vt-avail-tstart") ??
      root.querySelector<HTMLElement>(".vt-avail-tstart") ??
      root.querySelector<HTMLElement>(".vt-avail-any input:not(:disabled)") ??
      root.querySelector<HTMLElement>('.vt-avail-bandlist [role="radio"][tabindex="0"]') ??
      root.querySelector<HTMLElement>(".vt-avail-timeline .vt-avail-link:not(:disabled)") ??
      root.querySelector<HTMLElement>('.vt-avail-day[tabindex="0"]') ??
      root.querySelector<HTMLElement>(".vt-avail-day-tiles button");
    target?.focus();
  }, [refocusTick]);

  // A press on the not-ready Add: scroll to what is missing, focus its first
  // control, and highlight it for a moment (a colour, never motion under
  // prefers-reduced-motion). It never adds anything.
  const flashRef = useRef<{ el: HTMLElement; timer: ReturnType<typeof setTimeout> } | null>(null);
  useEffect(
    () => () => {
      const f = flashRef.current;
      if (f) {
        clearTimeout(f.timer);
        f.el.removeAttribute("data-vt-flash");
      }
    },
    [],
  );
  const flash = (el: HTMLElement) => {
    const prev = flashRef.current;
    if (prev) {
      clearTimeout(prev.timer);
      prev.el.removeAttribute("data-vt-flash");
    }
    // Restart the highlight on a second press.
    void el.offsetWidth;
    el.setAttribute("data-vt-flash", "");
    flashRef.current = {
      el,
      timer: setTimeout(() => {
        el.removeAttribute("data-vt-flash");
        if (flashRef.current?.el === el) flashRef.current = null;
      }, 1600),
    };
  };
  const showNext = () => {
    const root = panelRef.current;
    const n = next;
    if (!root || n.ready || !n.target) return;
    const q = (sel: string) => root.querySelector<HTMLElement>(sel);
    let focus: HTMLElement | null = null;
    let region: HTMLElement | null = null;
    if (n.target === "options") {
      region = q(".vt-avail-option-row");
      focus = q('.vt-avail-options > .vt-option-card[tabindex="0"]');
    } else if (n.target === "party") {
      const i = service.units.findIndex((u) => u.id === n.unitId);
      const cardEl = i >= 0 ? root.querySelectorAll<HTMLElement>(".vt-avail-options > .vt-option-card")[i] : undefined;
      region = cardEl ?? q(".vt-avail-option-row");
      focus =
        cardEl?.querySelector<HTMLElement>(`.vt-stepper button[aria-label="${n.adjust === "fewer" ? "Decrease" : "Increase"}"]`) ??
        cardEl ??
        null;
    } else if (n.target === "enquire") {
      focus = q(".vt-avail-step-when .vt-error-enquire-cta");
      region = focus;
    } else if (n.target === "days") {
      // Another day of this page that offers something (its first start, or
      // the day itself), else "More days →".
      const other = [...root.querySelectorAll<HTMLElement>(".vt-avail-day-tiles [data-day]:not(.vt-avail-day-on)")].find(
        (d) => /\bvt-avail-day-(free|together|apart|unknown)\b/.test(d.className),
      );
      const more = q('.vt-avail-day-nav .vt-avail-link:last-of-type:not(:disabled):not([aria-disabled="true"])');
      focus = other ? (other.querySelector<HTMLElement>(".vt-avail-tstart") ?? other) : (more ?? q(".vt-avail-day-tiles [data-day]"));
      region = other || !more ? q(".vt-avail-day-row") : more;
    } else if (n.target === "starts") {
      focus =
        q('.vt-avail-day-on .vt-avail-tstart[aria-pressed="true"]') ??
        q(".vt-avail-day-on .vt-avail-tstart") ??
        q('.vt-avail-starts [role="radio"][tabindex="0"]') ??
        q('.vt-avail-starts [role="radio"]') ??
        q(".vt-avail-any input:not(:disabled)") ??
        q('.vt-avail-bandlist [role="radio"][tabindex="0"]');
      region = focus?.closest<HTMLElement>(".vt-avail-day-on, .vt-avail-startpicker, .vt-avail-start-row") ?? focus;
    } else if (n.target === "together") {
      region = q(".vt-avail-together");
      focus = q(".vt-avail-together-btn:not(:disabled)");
    }
    const at = region ?? focus;
    if (!at) return;
    const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    at.scrollIntoView({ block: "nearest", inline: "nearest", behavior: reduce ? "auto" : "smooth" });
    focus?.focus({ preventScroll: true });
    flash(at);
  };

  const latestAddRef = useRef<() => void>(() => {});
  async function handleAdd() {
    if (inFlightRef.current || !next.ready || !day || startMs == null || entries.length === 0) return;
    // Either Add: the summary's, or the phone bar's (VisitSummary).
    const active = document.activeElement;
    const addHadFocus = !!active && active.classList.contains("vt-avail-add") && !!panelRef.current?.contains(active);
    // The page can outlive a start (bands answer for the moment they were read).
    if (startMs < Date.now()) {
      setStartMs(null);
      setNotice({ tone: "info", text: `${formatHmUnambiguous(startMs, tz)} has passed — pick a later time.` });
      if (addHadFocus) refocusStarts();
      return;
    }
    // Read the cart at the moment of the Add: hold only what is missing at
    // this start, and stamp the whole visit (its lines already held included).
    const inVisitNow = visitLinesAt(cartItemsRef.current, service.id, startMs);
    const plan = planVisitAdd(entries, inVisitNow);
    if (plan.toHold.length === 0) return;
    if (
      requestDateConfirm(() => latestAddRef.current(), {
        window: { starts_at: zonedNoonIso(day, tz) },
        exempt: dayChosenByGuest,
      })
    ) {
      return;
    }
    inFlightRef.current = true;
    setNotice(null);
    const hm = formatHmUnambiguous(startMs, tz);
    const addDay = day;
    const guests = plan.visitGuests;
    try {
      const outcome = await placeVisit({
        service,
        entries: plan.toHold,
        extraVisitGuests: plan.heldGuests,
        startMs,
        tz,
        recheck: () => bands.recheckDay(addDay),
        fallbackPrice: unitPrice,
        lengthLabel: (m) => lengthLabel(service, m),
        brandName: activeBrand?.name ?? null,
        onPhase: (p) => {
          if (mountedRef.current) setPhase(p);
        },
      });
      if (outcome.kind === "taken") {
        if (mountedRef.current) {
          setStartMs(null);
          setNotice({ tone: "info", text: `${hm} was just taken — here's what's free now.` });
          if (addHadFocus) refocusStarts();
        }
        return;
      }
      if (outcome.kind === "placed") {
        // Real holds exist now: the lines go into the cart even if this panel
        // unmounted mid-flight. Then the party is stamped once, for the visit.
        for (const line of outcome.lines) add(line);
        stampVisitPartySize(guests);
        // The stamp wrote the visit's headcount to the lines already held at
        // this start too: their cart copies say the same.
        for (const l of inVisitNow) patchMetadata(l.id, { visit_guests: guests });
        if (mountedRef.current) setStartMs(null);
        onAddedToCart?.();
        return;
      }
      // Refused: every hold placed for this Add was released — except any whose
      // release could not be confirmed, which stays visible in the visit.
      for (const k of outcome.kept) add(k.line);
      const refusal: PanelNotice = {
        tone: "error",
        text: refusalMessage({
          entry: outcome.entry,
          failure: outcome.failure,
          hm,
          noun,
          kept: outcome.kept.map((k) => k.entry),
          participantForms,
        }),
        enquiry: outcome.failure.reason === "oversize_party_enquiry" && !!onEnquire,
      };
      if (mountedRef.current) {
        setNotice(refusal);
        // A kept option is already in the visit: take it out of the choice,
        // so pressing Add again books only what is still missing — never a
        // second hold of the same option.
        if (outcome.kept.length > 0) {
          const keptUnits = new Set(outcome.kept.map((k) => k.entry.unit.id));
          setCounts((prev) => {
            const next = { ...prev };
            for (const id of keptUnits) delete next[id];
            return next;
          });
        }
      } else if (outcome.kept.length > 0) {
        // The guest left this tab mid-Add, and a line they did not ask for in
        // this form is now in their visit: say so where they are, and again
        // when they come back.
        pushToast(refusal.text, "warn");
        deliverOrphanNotice(service.id, refusal);
      }
      bands.refetch();
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setPhase("idle");
    }
  }
  latestAddRef.current = () => {
    void handleAdd();
  };

  // ── Guest actions ─────────────────────────────────────────────────────────
  const touched = () => setNotice(null);
  // The guest chose this day (a tile, the date jump, "try the next day"): no
  // confirm-date prompt for it, and it becomes the trip date every other tab
  // opens on.
  const pickDay = (d: string) => {
    touched();
    setDay(d);
    setDayChosenByGuest(true);
    setTripDateFromIso(zonedNoonIso(d, tz));
    setBandOpensMs(null);
    setStartMs(null);
  };
  // "More days" / "← Earlier" select the new page's first day FOR the guest:
  // still a panel pick, so Add asks to confirm it (owner rule), and it is not
  // pinned as the trip date.
  const goPage = (from: string) => {
    const next = clampJump(from, todayYmd, endYmd);
    touched();
    setPageFrom(next);
    setDay(next);
    setDayChosenByGuest(false);
    seedPanelDay(next);
    setBandOpensMs(null);
    setStartMs(null);
  };
  const jumpTo = (ymd: string) => {
    const d = clampJump(ymd, todayYmd, endYmd);
    setPageFrom(d);
    pickDay(d);
  };
  // A start the guest picked (a tile's start, a start chip): once, for that
  // pick, Add is brought into view if it is off screen, and its readiness is
  // announced (the effect after the render, below). A typed "any time" is
  // not a pick here: the page never moves under a guest who is typing.
  const [pick, setPick] = useState<{ seq: number; day: string; startMs: number } | null>(null);
  const pickSeqRef = useRef(0);
  const notePick = (d: string, ms: number) => setPick({ seq: ++pickSeqRef.current, day: d, startMs: ms });
  // A start tapped in a day's tile: that day (a guest pick — no confirm-date
  // prompt) and that start, in one step.
  const pickTileStart = (d: string, ms: number) => {
    if (busy) return;
    pickDay(d);
    setStartMs(ms);
    notePick(d, ms);
  };
  const toggleTileMore = (d: string) => setExpandedDays((prev) => ({ ...prev, [d]: !prev[d] }));
  // "Go together" / "Keep …": the choice becomes that one option — the whole
  // party, or its own headcount — on the SAME day; the other options are
  // unticked. Keyboard focus, which the pressed button takes with it as the
  // nudge gives way to the day's times, goes to those times once they show
  // (within a few seconds of the press, and never away from where the guest
  // has since put it — refocusStarts checks).
  const refocusAfterTogetherRef = useRef<number | null>(null);
  const takeTogether = (a: TogetherAction, e: MouseEvent<HTMLButtonElement>) => {
    if (busy) return;
    refocusAfterTogetherRef.current = document.activeElement === e.currentTarget ? Date.now() : null;
    touched();
    setCounts({ [a.unitId]: a.guests });
    setBandOpensMs(null);
    setStartMs(null);
  };
  useEffect(() => {
    const pressedAt = refocusAfterTogetherRef.current;
    if (pressedAt == null || dayStat === "checking") return;
    refocusAfterTogetherRef.current = null;
    if (Date.now() - pressedAt < 10_000) refocusStarts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayStat]);
  const nextDay = day && day < endYmd ? addDaysYmd(day, 1) : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  const heroImage = service.hero_image_url ?? (heroBrand ?? activeBrand)?.hero_image_url ?? null;
  // The VENUE's weekday (hoursPillText reads a Date's browser-local day).
  const heroHoursPill = hoursPillText(hours, browserDateOfYmd(todayYmd));
  const inCartSummary = renderInCartSummary(cartItems, service.id, linkPromo);
  const dayText = day ? dayLabel(day, todayYmd) : null;
  const shorterExists =
    shape === "sessions"
      ? !!sessionUnit &&
        service.units.some((u) => u.id !== sessionUnit.id && offered(u) && lengthFor(u) < lengthFor(sessionUnit))
      : entries.some((e) => e.variableLength && optionDurationChoices(e.unit).some((m) => m < e.minutes));
  const closure = day
    ? bw.closures.find((c) => c.guest_message && (c.starts_on ?? "") <= day && day <= (c.ends_on ?? c.starts_on ?? ""))
    : undefined;
  const dayAloneNow = day && (dayStat === "together" || dayStat === "apart") ? aloneOn(day) : null;
  const nudge =
    dayText && dayAloneNow && dayAloneNow !== "pending"
      ? togetherNudge({ dayShort: dayText.short, noun, options: togetherOptions, alone: dayAloneNow })
      : null;
  const emptyCopy = nudge
    ? nudge.text
    : dayText && dayStat === "unknown" && unitSchedule.status === "error"
      ? `We couldn't check which options are offered on ${dayText.short}. Retry or choose another day.`
    : day && dayText && dayStat && (dayStat === "full" || dayStat === "closed" || dayStat === "not_offered" || dayStat === "notice")
      ? emptyDayCopy({
          dayShort: dayText.short,
          status: dayStat,
          shape,
          noun,
          entries: entries.map((e) => ({
            name: e.unit.name,
            guests: e.guests,
            lengthLabel: e.variableLength ? lengthLabel(service, e.minutes) : null,
          })),
          sessionMinutes: shape === "sessions" ? longestMin || null : null,
          shorterExists,
          otherDaysExist: day < endYmd || day > todayYmd,
          noticeMin,
          closureMessage: closure?.guest_message ?? null,
          notOfferedNames: entries.filter((e) => notOfferedFor(day).includes(e.unit.id)).map((e) => e.unit.name),
        })
      : null;
  const oversizeCopy = oversize
    ? emptyDayCopy({
        dayShort: "",
        status: "oversize",
        shape,
        noun,
        entries: [],
        sessionMinutes: null,
        shorterExists: false,
        otherDaysExist: false,
        noticeMin,
        closureMessage: null,
        oversizeMax: enquiryAbove,
      })
    : null;
  // Announces the day's answer; the summary announces its own notices.
  const liveText =
    (!bandSelection || !day || !dayText
      ? ""
      : bandsStatus === "loading"
        ? "Checking availability…"
        : bandsStatus === "error"
          ? // A day the panel knows is closed or too soon says so, as shown.
            (emptyCopy ?? `Couldn't check availability for ${dayText.short}`)
          : dayStat === "free"
            ? dayFixed
              ? `${dayFixed.length} start time${dayFixed.length === 1 ? "" : "s"} on ${dayText.short}`
              : `${pickable.length} start window${pickable.length === 1 ? "" : "s"} on ${dayText.short}`
            : (emptyCopy ?? ""));

  const summaryLines: SummaryLine[] = entries.map((e) => ({
    key: e.unit.id,
    name: e.unit.name,
    lengthText: lengthLabel(service, e.minutes),
    guests: e.guests,
    noun,
    price: unitPrice(e.unit),
    basis: e.basis,
    currency: e.unit.currency ?? tenant.currency ?? "ZAR",
    inVisitGuests: alreadyInIds.has(e.unit.id) ? inVisitGuestsOf(e.unit.id) : null,
  }));
  const whenText =
    day && dayText && startMs != null
      ? `${dayText.short} · ${formatHmUnambiguous(startMs, tz)}`
      : dayText
        ? dayText.short
        : null;

  // What still stands between the guest and Add (next-step-logic.ts), in the
  // order they meet it: the summary's next-step line, where a press on the
  // not-ready Add takes them, and the step headings' ticks.
  // (Read by handleAdd and showNext, which run only after this render.)
  const next: NextStep = nextStep({
    shape,
    noun,
    entries: entries.map((e) => ({
      unitId: e.unit.id,
      name: e.unit.name,
      guests: e.guests,
      min: minFor(e.unit),
      max: capFor(e.unit),
    })),
    maxEntries: MAX_ENTRIES,
    overBlock,
    blockCap,
    oversize,
    canEnquire: !!onEnquire,
    dayShort: day && dayText ? dayText.short : null,
    checking: bandsStatus === "loading" || dayStat === "checking",
    startsOffered: dayStat === "free" || failOpen,
    together: nudge && nudge.actions.length > 0 ? nudge.status : null,
    dayMessage: nudge ? null : emptyCopy,
    startHm: startMs != null ? formatHmUnambiguous(startMs, tz) : null,
    alreadyIn: startMs != null && visitPlan.toHold.length === 0,
  });
  // The pick that made Add ready: announced through the bar's polite live
  // region while it stands (focus stays on the start).
  const readyNote =
    next.ready && pick && pick.startMs === startMs && pick.day === day && dayText
      ? readyAnnouncement(dayText.short, formatHmUnambiguous(pick.startMs, tz))
      : null;
  // …and, once for that pick, Add is brought into view when it is not wholly
  // on screen (owner, 25 Sept: "When a time is chosen and the booking button
  // is not on screen, scroll to it"). The bar sticks to the bottom of the
  // window, so on the widget's own page Add is already there; this is for a
  // page where the bar cannot stick — an ancestor that clips, or an iframe
  // as tall as the widget, where the HOST page scrolls. "Wholly on screen"
  // is an IntersectionObserver's answer, which inside an iframe is measured
  // against the top page's viewport, and scrollIntoView scrolls the frames
  // around the widget too. Never on a re-render or any other change.
  const handledPickRef = useRef(0);
  useEffect(() => {
    if (!pick || handledPickRef.current === pick.seq) return;
    handledPickRef.current = pick.seq;
    if (!next.ready) return;
    const bar = panelRef.current?.querySelector<HTMLElement>(".vt-avail-mbar");
    const addEl = bar?.querySelector<HTMLElement>(".vt-avail-add");
    if (!bar || !addEl) return;
    const reduce =
      typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const go = (box: DOMRectReadOnly, visible: DOMRectReadOnly | null) => {
      const plan = addRevealPlan({
        ready: true,
        box: { width: box.width, height: box.height },
        visible: visible ? { width: visible.width, height: visible.height } : null,
        reducedMotion: reduce,
      });
      if (plan) bar.scrollIntoView({ block: plan.block, inline: "nearest", behavior: plan.behavior });
    };
    if (typeof IntersectionObserver === "undefined") {
      const r = addEl.getBoundingClientRect();
      const top = Math.max(0, r.top);
      const bottom = Math.min(window.innerHeight, r.bottom);
      go(r, bottom > top ? new DOMRect(r.left, top, r.width, bottom - top) : null);
      return;
    }
    let live = true;
    let answered = false;
    const io = new IntersectionObserver((entries) => {
      io.disconnect();
      const e = entries[entries.length - 1];
      if (!live || !e) return;
      answered = true;
      go(e.boundingClientRect, e.isIntersecting ? e.intersectionRect : null);
    });
    io.observe(addEl);
    return () => {
      live = false;
      io.disconnect();
      // Torn down before the observer answered (an effect run twice): the
      // next run of this effect for the same pick asks again.
      if (!answered && handledPickRef.current === pick.seq) handledPickRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick?.seq]);

  const stepMark = (n: number, state: StepState) => (
    <span className={`vt-avail-step-num vt-avail-step-${state}`} aria-hidden="true">
      {state === "done" ? "✓" : n}
    </span>
  );

  return (
    <section className="vt-panel vt-avail" style={panelStyle} ref={panelRef}>
      {continuingFromServiceName && (
        <ContinuingBanner
          continuingFromServiceName={continuingFromServiceName}
          onDismissContinuing={onDismissContinuing}
        />
      )}
      <div className="vt-service-card">
        <ServiceHero
          service={service}
          heroImage={heroImage}
          heroStyle={heroStyle}
          heroHoursPill={heroHoursPill}
          capacityRemainingText={null}
          heroPricePill={null}
          cta={null}
        />
        <div className="vt-service-body">
          {inCartSummary && <div className="vt-top-actions">{inCartSummary}</div>}
          {service.description && <p className="vt-service-description">{service.description}</p>}
          <ol className="vt-avail-steps">
            <li className="vt-avail-step">
              <h3 className="vt-avail-step-title">
                {stepMark(1, next.steps.what)}
                What
                <span className="vt-avail-sr">{stepStateSpoken(next.steps.what)}</span>
                {stepMark(2, next.steps.howMany)}
                How many
                <span className="vt-avail-sr">{stepStateSpoken(next.steps.howMany)}</span>
              </h3>
              <WhatStep
                service={service}
                shape={shape}
                noun={noun}
                units={service.units}
                offered={offered}
                sessionUnitId={sessionUnit?.id ?? null}
                revealUnitId={linkedUnitId}
                headcount={sessionUnit ? (chosenCounts[sessionUnit.id] ?? headcount) : headcount}
                counts={chosenCounts}
                tickCapReached={shape === "multi" && Object.keys(chosenCounts).length >= MAX_ENTRIES}
                maxEntries={MAX_ENTRIES}
                capFor={capFor}
                minFor={minFor}
                // The ceiling the card states is the one the stepper stops at
                // (a "block" oversize limit included).
                partyMaxFor={(u) => (partyMaxFor(service, u) == null && blockCap == null ? null : capFor(u))}
                lengthFor={lengthFor}
                lengthChoices={optionDurationChoices}
                formatLength={(m) => lengthLabel(service, m)}
                displayPrice={(u) => {
                  if (!showPrices) return null;
                  const p = unitPrice(u);
                  return p != null ? Math.round(p) : null;
                }}
                hidePrice={!showPrices}
                priceSuffix={(u) => optionPriceSuffix(service, u)}
                priceFailed={(u) => showPrices && priceWindow != null && u.base_price != null && prices.hasFailed(u.id)}
                onPriceRetry={prices.retry}
                disabled={busy}
                onPickSession={(u) => {
                  touched();
                  setSessionUnitId(u.id);
                  setHeadcount((h) => Math.min(Math.max(minFor(u), h), capFor(u)));
                }}
                onHeadcount={(n) => {
                  touched();
                  setHeadcount(n);
                }}
                onToggle={(u) => {
                  touched();
                  setCounts((prev) => {
                    const next = { ...prev };
                    if ((next[u.id] ?? 0) > 0) delete next[u.id];
                    // A ticked option starts at its minimum ("2–4 riders": 2).
                    else next[u.id] = minFor(u);
                    return next;
                  });
                }}
                onCount={(u, n) => {
                  touched();
                  setCounts((prev) => ({ ...prev, [u.id]: n }));
                }}
                onLength={(u, m) => {
                  touched();
                  setUnitMinutes((prev) => ({ ...prev, [u.id]: m }));
                  if (shape === "sessions") setSessionUnitId(u.id);
                }}
              />
            </li>
            <li className="vt-avail-step vt-avail-step-when">
              <h3 className="vt-avail-step-title">
                {stepMark(3, next.steps.when)}
                When
                <span className="vt-avail-sr">{stepStateSpoken(next.steps.when)}</span>
              </h3>
              {oversize ? (
                <div className="vt-avail-empty" role="status">
                  {oversizeCopy}
                  {onEnquire && (
                    <>
                      {" "}
                      <button type="button" className="vt-cta-inline vt-error-enquire-cta" onClick={onEnquire}>
                        Enquire about your group →
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {!bandSelection && (
                    <p className="vt-avail-hint">
                      {tooMany
                        ? `Choose up to ${MAX_ENTRIES} options for one visit — then we'll show what's free.`
                        : "Choose what you'd like and how many first — then we'll show what's free."}
                    </p>
                  )}
                  <DayStrip
                    days={page.days}
                    statuses={statuses}
                    selected={day}
                    todayYmd={todayYmd}
                    hasMore={page.hasMore}
                    canBack={pageFrom > todayYmd}
                    minYmd={todayYmd}
                    maxYmd={endYmd}
                    disabled={busy}
                    onSelect={pickDay}
                    onMore={() => goPage(addDaysYmd(page.days[page.days.length - 1] ?? pageFrom, 1))}
                    onBack={() => {
                      const back = addDaysYmd(pageFrom, -DAYS_PER_PAGE);
                      goPage(back < todayYmd ? todayYmd : back);
                    }}
                    onJump={jumpTo}
                    noun={noun}
                    mode={mode}
                    tiles={tiles}
                    selectedStart={startMs}
                    tz={tz}
                    onPickStart={pickTileStart}
                    onToggleMore={toggleTileMore}
                  />
                  {day && bandSelection && dayStat && !dayInTile && (
                    <DayTimeline
                      day={day}
                      todayYmd={todayYmd}
                      tz={tz}
                      offsetAt={offsetAt}
                      windows={windowsFor(day)}
                      bands={dayBands}
                      status={dayStat}
                      noticeKnown={noticeKnown}
                      noticeMin={noticeMin}
                      longestMin={longestMin}
                      gran={gran}
                      shape={shape}
                      entryCount={entries.length}
                      selectedBand={selectedBand}
                      onSelectBand={(b) => {
                        touched();
                        setBandOpensMs(b.opensAtMs);
                        if (startMs != null && !(b.opensAtMs <= startMs && startMs <= b.lastStartMs)) setStartMs(null);
                      }}
                      emptyCopy={emptyCopy}
                      emptyActions={
                        nudge && nudge.actions.length > 0 ? (
                          <div
                            className="vt-avail-together"
                            role="group"
                            aria-label={nudge.status === "together" ? `${togetherVerb(noun) === "ride" ? "Ride" : "Go"} together` : "Keep one"}
                          >
                            {nudge.actions.map((a) => (
                              <button
                                key={`${a.kind}:${a.unitId}`}
                                type="button"
                                className="vt-avail-together-btn"
                                disabled={busy}
                                onClick={(e) => takeTogether(a, e)}
                              >
                                {a.label}
                              </button>
                            ))}
                          </div>
                        ) : null
                      }
                      onRetry={() => { unitSchedule.refetch(); bands.refetch(); }}
                      onNextDay={
                        nextDay
                          ? () => {
                              // The guest asked for this day: a guest pick.
                              if (!page.days.includes(nextDay)) setPageFrom(nextDay);
                              pickDay(nextDay);
                            }
                          : undefined
                      }
                      disabled={busy}
                      nowMs={now}
                      fixedStarts={dayFixed != null}
                      bare={mode === "starts" && dayStat !== "free"}
                    />
                  )}
                  {day && bandSelection && (dayStat === "free" || failOpen) && !dayInTile && (
                    <StartPicker
                      // A band the guest switches to starts a fresh picker: a
                      // time typed for another band never lingers in the field.
                      key={`${day}|${gran}|${bandOpensMs ?? ""}`}
                      band={failOpen ? null : selectedBand}
                      fixedStarts={failOpen ? null : dayFixed}
                      failOpenWindows={failOpen ? windowsFor(day) : null}
                      gran={gran}
                      tz={tz}
                      offsetAt={offsetAt}
                      day={day}
                      nowMs={now}
                      noticeMin={noticeMin}
                      longestMin={longestMin}
                      value={startMs}
                      onChange={(ms) => {
                        touched();
                        setStartMs(ms);
                        const typing = document.activeElement instanceof HTMLInputElement;
                        if (ms != null && !typing) notePick(day, ms);
                      }}
                      disabled={busy}
                    />
                  )}
                </>
              )}
              <div className="vt-avail-sr" aria-live="polite">
                {liveText}
              </div>
            </li>
          </ol>
        </div>
      </div>
      <VisitSummary
        lines={summaryLines}
        whenText={whenText}
        showPrices={showPrices}
        payAtVenue={service.pay_at_venue === true}
        notice={notice}
        onEnquire={onEnquire}
        ready={next.ready}
        hint={next.text}
        announceHint={next.announce}
        readyNote={readyNote}
        phase={phase}
        onAdd={() => latestAddRef.current()}
        onShowNext={showNext}
        afterPromo={
          // Several lines in one Add with a minimum spend: the server judges
          // it on the first held line only, so no line claims it here.
          linkPicked &&
          !(
            linkPicked.minSpend &&
            linkPicked.terms.min_spend_cents != null &&
            summaryLines.filter((l) => l.inVisitGuests == null).length > 1
          )
            ? (totalCents) =>
                pickedLineAfterLinkPromo(
                  linkPicked.terms,
                  { serviceId: service.id, serviceType: service.service_type, totalCents },
                  { minSpend: linkPicked.minSpend },
                )
            : null
        }
        promoCode={linkPicked?.terms.code ?? null}
      />
      {dateConfirm && day && (
        <SeededDateConfirmModal
          dateConfirm={dateConfirm}
          accentRef={panelRef}
          isAccommodation={false}
          serviceName={service.name}
          onCancel={() => setDateConfirm(null)}
          dateOnly={{ dayLabel: dayLabel(day, todayYmd).short }}
        />
      )}
    </section>
  );
}
